import React, { useEffect, useMemo, useState } from 'react';
import { CheckboxProps, Form, Typography } from 'antd';
import { FormLabel } from '../../../../components/Antd/FormLabel';
import { getMultValueFromEvent } from '../../../../helper';
import { IFieldValue, useScriptForm } from '../../ScriptCreateStore';
import { AccountStatus } from '../../../../types';
import { useAppContext } from '../../../../context/AppContext';
import { useAuth } from '../../../../context/AuthContext';
import styled from 'styled-components';
import { PageContent } from '../../../../assets/styles/Styled';
import { CloseOutlined } from '@ant-design/icons';
import {
  CustomTag,
  SelectOptionsUser,
  ItemFieldsWrapper,
  ScriptSectionWrapper,
  StyledInput,
  StyledSelect,
  StyledSharingButton,
  SharingList
} from '../sections.styled';
import useMedia from '../../../../hooks/useMedia';

const Container = styled(PageContent)`
  margin: 0 auto;
  overflow-y: auto;
`;

type Field = 'users' | 'organizations';

export const NameAndShareSection = () => {
  const { user } = useAuth();
  const { accounts, organizations, accountsLoading, organizationsLoading } = useAppContext();
  const [allowDuplicate, setAllowDuplicate] = useState(false);

  const {
    formValues,
    setFormValues,
    setSelectedUserValues,
    setSelectedOrganizationsValues,
    setSharingList,
    sharingList,
    selectedUserValues,
    selectedOrganizationsValues
  } = useScriptForm();
  const [form] = Form.useForm();

  const { isMobile } = useMedia();

  useEffect(() => {
    form.setFieldsValue(formValues);
  }, [form, formValues]);

  useEffect(() => {
    const userValues = sharingList.users.map(value => value.value);
    const organizationValues = sharingList.organizations.map(value => value.value);
    setSelectedUserValues(userValues);
    setSelectedOrganizationsValues(organizationValues);
  }, [sharingList, setSelectedUserValues, setSelectedOrganizationsValues]);

  const organizationOptions = useMemo(() => {
    return organizations.map(org => ({
      label: <SelectOptionsUser>{org.organization_name}</SelectOptionsUser>,
      value: org.organization_id,
      desc: org.organization_name
    }));
  }, [organizations]);

  const mapanderOptions = useMemo(() => {
    const options = accounts
      ?.filter(acc => acc.status === AccountStatus.Active)
      .map(acc => ({
        label: (
          <SelectOptionsUser>
            {`${acc.first_name || ''} ${acc.last_name || ''}`}
            <span className="tag-email">{acc.email}</span>
          </SelectOptionsUser>
        ),
        value: acc.user_id,
        desc: `${acc.first_name || ''} ${acc.last_name || ''} ${acc.email}`
      }));
    return options?.length
      ? options
      : user
        ? [
            {
              label: (
                <SelectOptionsUser>
                  {`${user.user.first_name || ''} ${user.user.last_name || ''}`}
                  <span className="tag-email">{user.user.email}</span>
                </SelectOptionsUser>
              ),
              value: user.user.user_id,
              desc: `${user.user.first_name || ''} ${user.user.last_name || ''} ${user.user.email}`
            }
          ]
        : [];
  }, [accounts, user]);
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const closeIcon = <CloseOutlined className="btn-close" style={{ fontSize: '18px', marginLeft: '13px', color: '#AFCCD2' }} />;

  const handleAddToSharing = (e: React.MouseEvent<HTMLButtonElement>, field: Field) => {
    e.preventDefault();
    const value = form.getFieldValue(field);
    setSharingList(prev => ({
      ...prev,
      [field]: [...prev[field], ...value]
    }));
    form.setFieldValue(field, []);
    setFormValues(prev => ({ ...prev, [field]: [] }));
  };
  const onClose = (id: string, field: Field) => {
    const filteredValues = sharingList[field].filter((item: IFieldValue) => item.value !== id);
    setSharingList(prev => ({
      ...prev,
      [field]: filteredValues
    }));
  };
  const handleClearAll = (e: React.MouseEvent<HTMLButtonElement>, field: Field) => {
    e.preventDefault();
    setSharingList(prev => ({
      ...prev,
      [field]: []
    }));
  };
  const filteredUsersOptions = useMemo(() => {
    return {
      optionsSelect: mapanderOptions.filter(o => !selectedUserValues.includes(o.value)),
      optionsTag: mapanderOptions.filter(o => selectedUserValues.includes(o.value))
    };
  }, [selectedUserValues, mapanderOptions]);

  const filteredOrganizationOptions = useMemo(() => {
    return {
      optionsSelect: organizationOptions.filter(o => !selectedOrganizationsValues.includes(o.value)),
      optionsTag: organizationOptions.filter(o => selectedOrganizationsValues.includes(o.value))
    };
  }, [selectedOrganizationsValues, organizationOptions]);

  const onChange: CheckboxProps['onChange'] = e => {
    console.log(`checked = ${e.target.checked}`);
    setAllowDuplicate(e.target.checked);
  };

  return (
    <Container>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={changedValues => setFormValues(prev => ({ ...prev, ...changedValues }))}
      >
        <Form.Item
          label={<FormLabel text="Enter script name" />}
          name="script_name"
          rules={[{ required: true, message: 'Script name is required' }]}
        >
          <StyledInput size="large" placeholder="Enter script name" />
        </Form.Item>
        <FormLabel text="Sharing Script" />
        <ScriptSectionWrapper>
          <ItemFieldsWrapper>
            <Form.Item
              style={{ width: '100%', marginBottom: '0' }}
              label={<FormLabel text="Share With Accounts" />}
              name="users"
              getValueFromEvent={getMultValueFromEvent}
            >
              <StyledSelect
                mode="multiple"
                labelInValue
                options={filteredUsersOptions.optionsSelect}
                size="large"
                optionFilterProp="desc"
                placeholder={'Account'}
                loading={accountsLoading}
                value={sharingList}
              />
            </Form.Item>
            <StyledSharingButton disabled={!form.getFieldValue('users')?.length} onClick={e => handleAddToSharing(e, 'users')}>
              {isMobile ? 'Add' : 'Add To Sharing List'}
            </StyledSharingButton>
          </ItemFieldsWrapper>
          {!!filteredUsersOptions.optionsTag.length && !accountsLoading && (
            <SharingList>
              {filteredUsersOptions.optionsTag.map(item => (
                <CustomTag key={item.value} onMouseDown={onPreventMouseDown} onClick={() => onClose(item.value, 'users')}>
                  {item.label}
                  {closeIcon}
                </CustomTag>
              ))}
              <StyledSharingButton
                style={{ height: '40px', width: 'fit-content', minWidth: 'unset', margin: '4px 0 0' }}
                onClick={e => handleClearAll(e, 'users')}
              >
                Clear all
              </StyledSharingButton>
            </SharingList>
          )}

          <ItemFieldsWrapper className="mb-4">
            <Form.Item
              style={{ width: '100%', marginBottom: '0' }}
              label={<FormLabel text="Share with Organizations" />}
              name="organizations"
              getValueFromEvent={getMultValueFromEvent}
              hidden={!organizationOptions.length}
            >
              <StyledSelect
                mode="multiple"
                labelInValue
                options={filteredOrganizationOptions.optionsSelect}
                size="large"
                showSearch
                optionFilterProp="desc"
                placeholder="Organization name"
                loading={organizationsLoading}
              />
            </Form.Item>
            {!!organizationOptions.length && (
              <StyledSharingButton
                disabled={!form.getFieldValue('organizations')?.length}
                onClick={e => handleAddToSharing(e, 'organizations')}
              >
                {isMobile ? 'Add' : 'Add To Sharing List'}
              </StyledSharingButton>
            )}
          </ItemFieldsWrapper>
          {!!filteredOrganizationOptions.optionsTag.length && !accountsLoading && (
            <SharingList>
              {filteredOrganizationOptions.optionsTag.map(item => (
                <CustomTag
                  style={{ height: '40px' }}
                  key={item.value}
                  onMouseDown={onPreventMouseDown}
                  onClick={() => onClose(item.value, 'organizations')}
                >
                  {item.label}
                  {closeIcon}
                </CustomTag>
              ))}
              <StyledSharingButton
                style={{ height: '40px', width: 'fit-content', minWidth: 'unset' }}
                onClick={e => handleClearAll(e, 'organizations')}
              >
                Clear all
              </StyledSharingButton>
            </SharingList>
          )}
        </ScriptSectionWrapper>
        {/* <CustomCheckbox onChange={onChange} defaultChecked={allowDuplicate}>
          <Typography className="regular-16 light-description">Allow to duplicate script to those who have access</Typography>
        </CustomCheckbox> */}
        {/* 
        {!!organizationOptions.length && (
          <FormItemDescription text="Every member of the selected organizations will have access to this script" />
        )} */}
      </Form>
    </Container>
  );
};
